import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, Subscription, from, of } from 'rxjs';
import { catchError, concatMap, finalize, reduce, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  CompareService,
  DriftDashboardRow,
  DriftDetailRow,
  DriftGroupMemberRow
} from '../services/compare.service';
import { UserService } from '../services/user.service';
import { DatabaseService } from '../services/database.service';

interface HostSummary {
  hostname: string;
  serialNumber?: string;
  totalGroups: number;
  totalDifferences: number;
  rows: DriftDashboardRow[];
  expanded: boolean;
  loadingDetails: boolean;
  details?: DriftDetailRow[];
  detailsLoaded: boolean;
  detailsError?: string;
  diffTypeCounts: Record<string, number>;
}

interface GroupMemberState {
  groupName?: string;
  loading: boolean;
  error?: string;
  members: DriftGroupMemberRow[];
}

type DetailWithMembers = DriftDetailRow & { membersState?: GroupMemberState };

const DIFF_TYPE_META: Record<string, { label: string; className: string }> = {
  HASH_MISMATCH: { label: 'Mismatch', className: 'badge-mismatch' },
  EXTRA_IN_DEVICE: { label: 'Extra in Device', className: 'badge-extra-device' },
  EXTRA_DEVICE: { label: 'Extra in Device', className: 'badge-extra-device' },
  EXTRA_IN_GOLD: { label: 'Missing in Device', className: 'badge-extra-gold' },
  MISSING_IN_DEVICE: { label: 'Missing in Device', className: 'badge-extra-gold' },
  HASH_MATCH: { label: 'Matches', className: 'badge-match' },
  _default: { label: 'Other', className: 'badge-other' }
};

@Component({
  selector: 'app-compare',
  standalone: false,
  templateUrl: './compare.component.html',
  styleUrl: './compare.component.scss'
})
export class CompareComponent implements OnInit, OnDestroy {
  hostSummaries: HostSummary[] = [];
  loading = false;
  hasNoDifferences = false;
  error?: string;

  includeZeroDifferences = false;

  private readonly destroy$ = new Subject<void>();
  private readonly detailSubscriptions: Record<string, Subscription> = {};
  // Legacy host-level member state removed; we now attach member drift state per detail row.
  private readonly legacyMemberStateByHost: Record<string, GroupMemberState> = {};
  private readonly memberPrefetchSubscriptions: Record<string, Subscription> = {};

  private readonly maxGroupsPerMemberRequest = 25;

  constructor(
    private readonly compareService: CompareService,
    private readonly userService: UserService,
    private readonly databaseService: DatabaseService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.loadDashboard(false);
  }

  ngOnDestroy(): void {
    Object.values(this.detailSubscriptions).forEach(sub => sub.unsubscribe());
    // Per-detail subscriptions are one-off; nothing additional to clean besides detail subscriptions.
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh(): void {
    this.loadDashboard(true);
  }

  formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    if (Array.isArray(value)) {
      return value.length ? value.map(item => this.formatValue(item)).join(', ') : '—';
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (error) {
        console.error('Unable to stringify value', error);
        return String(value);
      }
    }

    return String(value);
  }

  trackByHost = (_: number, host: HostSummary): string => host.hostname;
  trackByDetail = (index: number, detail: DriftDetailRow): string =>
    `${detail.hostname ?? 'host'}::${detail.groupName ?? detail.label ?? 'group'}::${index}`;
  trackByMember = (index: number, member: DriftGroupMemberRow): string =>
    `${member.groupName ?? 'group'}::${member.objectName ?? member.label ?? index}`;

  getCurrentValue(row: Partial<DriftDashboardRow>): any {
    return row.currentValue ?? row.deviceValue ?? row.asaValue ?? null;
  }

  getExpectedValue(row: Partial<DriftDashboardRow>): any {
    return row.expectedValue ?? row.goldValue ?? null;
  }

  getDiffType(detail: DriftDetailRow | DriftGroupMemberRow): string {
    const diffType = (detail as any).DiffType ?? (detail as any).diffType;
    return diffType ? String(diffType) : '—';
  }

  getMemberDisplayName(member: DriftGroupMemberRow): string {
    const candidateKeys = ['objectName', 'label', 'identifier', 'interfaceName', 'name'];
    for (const key of candidateKeys) {
      const value = member[key];
      if (value !== undefined && value !== null && value !== '') {
        return String(value);
      }
    }
    return '—';
  }

  onIncludeZeroToggle(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.includeZeroDifferences = checkbox.checked;
    this.loadDashboard(true);
  }

  toggleHost(host: HostSummary): void {
    host.expanded = !host.expanded;
    if (host.expanded && !host.detailsLoaded) {
      this.loadDeviceDetailsForHost(host);
    }

    // If details are already available, fetch member drift in one batched call.
    if (host.expanded && host.detailsLoaded && host.details?.length) {
      this.prefetchGroupMembersForHost(host);
    }

    // No need to clear per-detail member state when collapsing; it remains cached.
  }

  fixIssue(detail: DriftDetailRow): void {
    const diffType = this.getDiffType(detail);
    const groupName = detail.groupName ?? detail.label ?? 'unknown';
    const hostname = detail.hostname ?? 'unknown';
    const membersState = (detail as any).membersState;

    // Helper to normalize values into name/type for summaries
    const parseValue = (value: any): { name: string; type: string } => {
      const raw = this.formatValue(value);
      if (!raw || raw === '—') return { name: '', type: 'object' };

      // Accept pipe-delimited prefixes coming from backend (e.g., host|1.2.3.4, group|name)
      if (raw.includes('|')) {
        const [maybeType, ...rest] = raw.split('|');
        const name = rest.join('|');
        const type = maybeType.toLowerCase();
        return { name, type };
      }

      // Fallback: best-effort type inference
      if (/^\d+\.\d+\.\d+\.\d+$/.test(raw)) return { name: raw, type: 'host' };
      if (/^[\w.-]+$/.test(raw)) return { name: raw, type: 'object' };
      return { name: raw, type: 'object' };
    };

    // Find the corresponding inventory item by hostname to set as selectedInventory
    this.databaseService.getAllInventory().subscribe((inventory: any[]) => {
      const selectedInventory = inventory.find((item: any) => item.hostname === hostname);
      
      if (!selectedInventory) {
        console.error('Could not find inventory item for hostname:', hostname);
        return;
      }

      // Generate the fix changes based on the diff type and members
      const changes: Array<{ summary: string; meta?: string }> = [];

      // Check if we have member-level differences
      if (membersState && Array.isArray(membersState.members) && membersState.members.length > 0) {
        // Process each member difference
        for (const member of membersState.members) {
          const memberDiffType = this.getMemberDiffType(member);
          const memberName = this.getMemberName(member);
          
          if (!memberName) continue;

          const parsed = parseValue(memberName);
          
          if (memberDiffType === 'MISSING_IN_DEVICE' || memberDiffType === 'EXTRA_IN_GOLD') {
            // Add the gold source value
            changes.push({
              summary: `Add ${parsed.type}: ${parsed.name}`,
              meta: `Add to ${groupName}`
            });
          } else if (memberDiffType === 'EXTRA_IN_DEVICE' || memberDiffType === 'EXTRA_DEVICE') {
            // Remove the device value
            changes.push({
              summary: `Remove ${parsed.type}: ${parsed.name}`,
              meta: `Remove from ${groupName}`
            });
          }
        }
      } else {
        // Fallback for group-level diffs without member details
        // This shouldn't happen often but keeps the original logic as backup
        const goldSourceValue = this.getExpectedValue(detail);
        const deviceValue = this.getCurrentValue(detail);
        const deviceParsed = parseValue(deviceValue);
        const goldParsed = parseValue(goldSourceValue);

        if (diffType === 'HASH_MISMATCH') {
          if (deviceParsed.name) {
            changes.push({
              summary: `Remove ${deviceParsed.type}: ${deviceParsed.name}`,
              meta: `Remove from ${groupName}`
            });
          }
          if (goldParsed.name) {
            changes.push({
              summary: `Add ${goldParsed.type}: ${goldParsed.name}`,
              meta: `Add to ${groupName}`
            });
          }
        } else if (diffType === 'EXTRA_IN_DEVICE' || diffType === 'EXTRA_DEVICE') {
          if (deviceParsed.name) {
            changes.push({
              summary: `Remove ${deviceParsed.type}: ${deviceParsed.name}`,
              meta: `Remove from ${groupName}`
            });
          }
        } else if (diffType === 'MISSING_IN_DEVICE' || diffType === 'EXTRA_IN_GOLD') {
          if (goldParsed.name) {
            changes.push({
              summary: `Add ${goldParsed.type}: ${goldParsed.name}`,
              meta: `Add to ${groupName}`
            });
          }
        }
      }

      // Update the filteredChanges$ observable with the changes for this group
      if (changes.length > 0) {
        const existing = this.userService.getFilteredChanges();
        const idx = existing.findIndex(g => g.groupName === groupName);
        const targetIndex = idx >= 0 ? idx : existing.length;
        this.userService.updateFilteredChanges(targetIndex, groupName, changes);
      }

      // Navigate to view component with the serialNumber as query param
      // The view component will auto-select this inventory when it loads
      this.router.navigate(['/view'], {
        queryParams: { serialNumber: selectedInventory.serialNumber }
      });
    });
  }

  getSeverityBadges(host: HostSummary): Array<{ key: string; label: string; count: number; className: string }> {
    const entries = Object.entries(host.diffTypeCounts ?? {}).filter(([, count]) => count > 0);
    if (!entries.length) {
      return [];
    }

    return entries
      .map(([key, count]) => {
        const meta = DIFF_TYPE_META[key] ?? DIFF_TYPE_META['_default'];
        return { key, label: meta.label, className: meta.className, count };
      })
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }

  getSummaryLine(host: HostSummary): string | null {
    const badges = this.getSeverityBadges(host);
    if (!badges.length) {
      return null;
    }

    return badges.map(badge => `${badge.label} (${badge.count})`).join(', ');
  }

  // Per-detail member state access helper
  private getDetailMemberState(detail: DriftDetailRow): GroupMemberState | undefined {
    return (detail as DetailWithMembers).membersState;
  }

  getMemberHostname(member: DriftGroupMemberRow, host: HostSummary): string {
    const source = member as Record<string, any>;
    return source['hostname'] ?? source['Hostname'] ?? host.hostname;
  }

  getMemberSerial(member: DriftGroupMemberRow, host: HostSummary): string | undefined {
    const source = member as Record<string, any>;
    return source['serialNumber'] ?? source['SerialNumber'] ?? host.serialNumber ?? undefined;
  }

  getMemberName(member: DriftGroupMemberRow): string {
    const source = member as Record<string, any>;
    return source['Member'] ?? source['member'] ?? this.getMemberDisplayName(member);
  }

  getMemberDiffType(member: DriftGroupMemberRow): string {
    const source = member as Record<string, any>;
    return source['MemberDiffType'] ?? source['memberDiffType'] ?? this.getDiffType(member);
  }

  getGroupDiffType(host: HostSummary, member: DriftGroupMemberRow, fallbackGroupName?: string): string {
    const source = member as Record<string, any>;
    const explicit = source['GroupDiffType'] ?? source['groupDiffType'];
    if (explicit) {
      return String(explicit);
    }

    const targetGroup = member.groupName ?? source['groupName'] ?? source['GroupName'] ?? fallbackGroupName;
    if (!targetGroup) {
      return '—';
    }

    const detailMatch = host.details?.find(detail => {
      const detailName = detail.groupName ?? (detail as any)['label'] ?? (detail as any)['group'];
      return detailName === targetGroup;
    });

    return detailMatch ? this.getDiffType(detailMatch) : '—';
  }

  // Automatically load member-level drift for a HASH_MISMATCH detail group.
  private autoLoadGroupMembers(host: HostSummary, detail: DriftDetailRow): void {
    if (!detail.groupName) {
      return;
    }
    const diffType = this.getDiffType(detail);
    if (diffType !== 'HASH_MISMATCH') {
      return; // Only load member diffs for mismatches.
    }
    const detailWithMembers = detail as DetailWithMembers;
    // Avoid re-fetching if already loaded or in-flight.
    if (detailWithMembers.membersState && (detailWithMembers.membersState.loading || detailWithMembers.membersState.members.length)) {
      return;
    }
    detailWithMembers.membersState = { groupName: detail.groupName, loading: true, members: [] };
    this.compareService
      .getGroupMemberDrift(host.hostname, detail.groupName)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (detailWithMembers.membersState) {
            detailWithMembers.membersState.loading = false;
          }
        })
      )
      .subscribe({
        next: members => {
          if (!detailWithMembers.membersState) {
            return;
          }
            detailWithMembers.membersState.members = Array.isArray(members) ? members : [];
            if (!detailWithMembers.membersState.members.length) {
              detailWithMembers.membersState.error = 'No member-level differences returned for this group.';
            }
        },
        error: error => {
          console.error('Failed to load group member drift', error);
          if (detailWithMembers.membersState) {
            detailWithMembers.membersState.error = 'Unable to load member-level differences.';
            detailWithMembers.membersState.members = [];
          }
        }
      });
  }

  private chunkArray<T>(items: T[], size: number): T[][] {
    if (!Array.isArray(items) || size <= 0) return [];
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  }

  private normalizeGroupName(value: any): string {
    return (value ?? '').toString().trim();
  }

  private extractMemberGroupName(member: DriftGroupMemberRow): string {
    const source = member as Record<string, any>;
    return this.normalizeGroupName(member.groupName ?? source['groupName'] ?? source['GroupName'] ?? source['group'] ?? source['Group']);
  }

  // Batch-load member-level drift for all HASH_MISMATCH groups for a host.
  private prefetchGroupMembersForHost(host: HostSummary): void {
    if (!host?.hostname || !Array.isArray(host.details) || !host.details.length) {
      return;
    }

    const mismatchDetails = host.details.filter(d => {
      const groupName = this.normalizeGroupName(d.groupName);
      if (!groupName) return false;
      const diffType = this.getDiffType(d);
      return diffType === 'HASH_MISMATCH';
    }) as DetailWithMembers[];

    if (!mismatchDetails.length) {
      return;
    }

    // Only request groups that are not already loaded/in-flight.
    const groupsToFetch: string[] = [];
    for (const detail of mismatchDetails) {
      const groupName = this.normalizeGroupName(detail.groupName);
      const existing = detail.membersState;
      if (existing?.loading) {
        continue;
      }
      if (existing?.members?.length) {
        continue;
      }
      // Initialize state so UI can show loading.
      detail.membersState = { groupName, loading: true, members: [] };
      groupsToFetch.push(groupName);
    }

    const uniqueGroups = Array.from(new Set(groupsToFetch)).filter(Boolean);
    if (!uniqueGroups.length) {
      return;
    }

    // Ensure only one in-flight prefetch per host.
    this.memberPrefetchSubscriptions[host.hostname]?.unsubscribe();

    const chunks = this.chunkArray(uniqueGroups, this.maxGroupsPerMemberRequest);
    const request$: Observable<DriftGroupMemberRow[]> = from(chunks).pipe(
      concatMap(groupChunk =>
        this.compareService.getGroupMemberDrift(host.hostname, groupChunk).pipe(
          catchError(error => {
            console.error('Failed to load group member drift (batched)', error);
            // Propagate an empty result for this chunk; we'll mark errors below.
            return of([] as DriftGroupMemberRow[]);
          })
        )
      ),
      reduce((all, nextMembers) => all.concat(Array.isArray(nextMembers) ? nextMembers : []), [] as DriftGroupMemberRow[])
    );

    this.memberPrefetchSubscriptions[host.hostname] = request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          // Clear loading state for any groups we attempted.
          for (const detail of mismatchDetails) {
            const groupName = this.normalizeGroupName(detail.groupName);
            if (!groupName) continue;
            if (groupsToFetch.includes(groupName) && detail.membersState) {
              detail.membersState.loading = false;
            }
          }
        })
      )
      .subscribe({
        next: allMembers => {
          const byGroup = new Map<string, DriftGroupMemberRow[]>();
          for (const m of allMembers ?? []) {
            const g = this.extractMemberGroupName(m);
            if (!g) continue;
            const list = byGroup.get(g) ?? [];
            list.push(m);
            byGroup.set(g, list);
          }

          for (const detail of mismatchDetails) {
            const groupName = this.normalizeGroupName(detail.groupName);
            if (!groupName || !detail.membersState) continue;
            if (!groupsToFetch.includes(groupName)) continue;

            const members = byGroup.get(groupName) ?? [];
            detail.membersState.members = members;
            if (!members.length) {
              detail.membersState.error = 'No member-level differences returned for this group.';
            } else {
              detail.membersState.error = undefined;
            }
          }
        }
      });
  }

  private loadDashboard(preserveState: boolean): void {
    const previousState = preserveState
      ? new Map(this.hostSummaries.map(summary => [summary.hostname, summary]))
      : new Map<string, HostSummary>();

    this.loading = true;
    this.hasNoDifferences = false;
    this.error = undefined;

    this.compareService
      .getDriftDashboard(this.includeZeroDifferences)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: rows => {
          const data = Array.isArray(rows) ? rows : [];
          const summaries = this.buildHostSummaries(data);
          this.hostSummaries = summaries.map(summary => {
            const previous = previousState.get(summary.hostname);
            if (previous) {
              summary.expanded = previous.expanded;
              summary.details = previous.details;
              summary.detailsLoaded = previous.detailsLoaded;
              summary.detailsError = previous.detailsError;
              summary.diffTypeCounts = previous.diffTypeCounts;
            }

            return summary;
          });

          if (!this.hostSummaries.length) {
            this.hasNoDifferences = true;
          } else {
            // Avoid auto-fetching details/members for every host.
            // Load details only when a host is expanded (or when preserving an expanded state).
            this.hostSummaries.forEach(host => {
              if (host.expanded && !host.detailsLoaded) {
                this.loadDeviceDetailsForHost(host);
              }
            });
          }

          const activeHosts = new Set(this.hostSummaries.map(host => host.hostname));
          Object.keys(this.detailSubscriptions).forEach(hostname => {
            if (!activeHosts.has(hostname)) {
              this.detailSubscriptions[hostname]?.unsubscribe();
              delete this.detailSubscriptions[hostname];
            }
          });
          // Legacy cleanup of host-level member state no longer required.
        },
        error: error => {
          console.error('Failed to load drift dashboard data', error);
          this.error = 'Unable to load comparison differences.';
          this.hostSummaries = [];
        }
      });
  }

  private buildHostSummaries(rows: DriftDashboardRow[]): HostSummary[] {
    const grouped = new Map<string, DriftDashboardRow[]>();
    rows.forEach(row => {
      if (!row || !row.hostname) {
        return;
      }
      const existing = grouped.get(row.hostname) ?? [];
      existing.push(row);
      grouped.set(row.hostname, existing);
    });

    const summaries = Array.from(grouped.entries()).map(([hostname, hostRows]) => {
      const serialEntry = hostRows.find(r => r && (r as any)['serialNumber']);
      const serialNumber = serialEntry ? (serialEntry as any)['serialNumber'] : undefined;
      const totalGroups = hostRows.length;
      // Count only rows whose diff type represents a meaningful difference (mismatch or extra in device)
      const totalDifferences = this.computeTotalDifferences(hostRows);

      return {
        hostname,
        serialNumber,
        totalGroups,
        totalDifferences,
        rows: hostRows,
        expanded: false,
        loadingDetails: false,
        detailsLoaded: false,
        diffTypeCounts: this.buildDiffTypeCounts(hostRows)
      } as HostSummary;
    });

    summaries.sort((a, b) => {
      if (b.totalDifferences !== a.totalDifferences) {
        return b.totalDifferences - a.totalDifferences;
      }
      return a.hostname.localeCompare(b.hostname);
    });

    return summaries;
  }

  private loadDeviceDetailsForHost(host: HostSummary, options: { prefetch?: boolean } = {}): void {
    if (host.loadingDetails || host.detailsLoaded) {
      return;
    }

    host.loadingDetails = true;
    host.detailsError = undefined;

    this.detailSubscriptions[host.hostname]?.unsubscribe();
    this.detailSubscriptions[host.hostname] = this.compareService
      .getDriftDetailsByHostname(host.hostname, this.includeZeroDifferences)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          host.loadingDetails = false;
        })
      )
      .subscribe({
        next: details => {
          host.details = Array.isArray(details) ? (details as DetailWithMembers[]) : [];
          host.detailsLoaded = true;
          if (!host.details.length) {
            host.detailsError = 'No group differences returned for this device.';
          }
          host.diffTypeCounts = this.buildDiffTypeCounts(host.rows, host.details);
          // Recalculate totalDifferences using more granular detail rows if available
          if (host.details?.length) {
            host.totalDifferences = this.computeTotalDifferences(host.details);
            this.resortHosts();
            // Only load member drift when the host is actually expanded (interactive view).
            if (!options.prefetch && host.expanded) {
              this.prefetchGroupMembersForHost(host);
            }
          }
        },
        error: error => {
          console.error('Failed to load drift details', error);
          host.detailsLoaded = false;
          host.detailsError = 'Unable to load group differences for this device.';
        }
      });
  }

  // Legacy compatibility for existing spec tests.
  viewGroupMembers(host: HostSummary, detail: DriftDetailRow): void {
    if (!host?.hostname || !detail?.groupName) {
      return;
    }

    const detailState = this.getDetailMemberState(detail);
    if (detailState?.members?.length) {
      this.legacyMemberStateByHost[host.hostname] = { ...detailState };
      return;
    }

    this.legacyMemberStateByHost[host.hostname] = { groupName: detail.groupName, loading: true, members: [] };
    this.compareService.getGroupMemberDrift(host.hostname, detail.groupName)
      .pipe(finalize(() => {
        const state = this.legacyMemberStateByHost[host.hostname];
        if (state) state.loading = false;
      }))
      .subscribe({
        next: members => {
          const state = this.legacyMemberStateByHost[host.hostname];
          if (!state) return;
          state.members = Array.isArray(members) ? members : [];
        },
        error: () => {
          const state = this.legacyMemberStateByHost[host.hostname];
          if (!state) return;
          state.error = 'Unable to load member-level differences.';
          state.members = [];
        }
      });
  }

  getGroupMemberState(host: HostSummary): GroupMemberState {
    if (!host?.hostname) {
      return { loading: false, members: [] };
    }
    return this.legacyMemberStateByHost[host.hostname] ?? { loading: false, members: [] };
  }

  private buildDiffTypeCounts(dashboardRows: DriftDashboardRow[] | undefined, detailRows?: DriftDetailRow[]): Record<string, number> {
    const counts: Record<string, number> = {};
    const accumulate = (entry: any) => {
      const key = this.normalizeDiffType(entry);
      if (!key) {
        return;
      }
      counts[key] = (counts[key] ?? 0) + 1;
    };

    let detailHasTypes = false;
    if (detailRows?.length) {
      detailRows.forEach(entry => {
        const before = Object.keys(counts).length;
        accumulate(entry);
        if (!detailHasTypes) {
          detailHasTypes = Object.keys(counts).length > before;
        }
      });
    }

    if (!detailHasTypes && dashboardRows?.length) {
      dashboardRows.forEach(accumulate);
    }

    return counts;
  }

  private normalizeDiffType(entry: Record<string, any> | undefined): string | undefined {
    if (!entry) {
      return undefined;
    }

    const raw = entry['DiffType'] ?? entry['diffType'] ?? entry['diff_type'] ?? entry['type'];
    if (!raw) {
      return undefined;
    }

    const normalized = String(raw).trim().toUpperCase();
    if (!normalized) {
      return undefined;
    }

    if (normalized === 'EXTRA DEVICE') {
      return 'EXTRA_IN_DEVICE';
    }
    if (normalized === 'EXTRA GOLD') {
      return 'EXTRA_IN_GOLD';
    }

    return normalized;
  }

  // Host-level member state clearing removed (per-detail caching retained across collapse/expand).

  // Counts only meaningful differences for totalDifferences summary.
  private computeTotalDifferences(rows: Array<Record<string, any>>): number {
    let count = 0;
    rows.forEach(r => {
      if (!r) return;

      // Prefer explicit issue counts when present (backend frequently supplies these).
      const issueCountRaw = (r as any)['issueCount'] ?? (r as any)['IssueCount'];
      const issueCount = typeof issueCountRaw === 'number' ? issueCountRaw : Number.parseInt(String(issueCountRaw ?? ''), 10);
      if (Number.isFinite(issueCount) && issueCount > 0) {
        count += issueCount;
        return;
      }

      // Fallback: count group-level differences based on diff type.
      const diffType = this.normalizeDiffType(r as any);
      if (!diffType) return;
      if (diffType === 'HASH_MISMATCH' || diffType === 'EXTRA_IN_DEVICE' || diffType === 'EXTRA_DEVICE') {
        count++;
      }
    });
    return count;
  }

  private resortHosts(): void {
    this.hostSummaries.sort((a, b) => {
      if (b.totalDifferences !== a.totalDifferences) {
        return b.totalDifferences - a.totalDifferences;
      }
      return a.hostname.localeCompare(b.hostname);
    });
  }

  canCopyHostDetails(host: HostSummary): boolean {
    if (!host?.expanded) {
      return false;
    }
    if (host.loadingDetails || !host.detailsLoaded) {
      return false;
    }
    if (!host.details?.length) {
      return false;
    }

    for (const detail of host.details) {
      const diffType = this.getDiffType(detail);
      if (diffType !== 'HASH_MISMATCH') {
        continue;
      }

      const groupName = this.normalizeGroupName(detail.groupName);
      if (!groupName) {
        continue;
      }

      const membersState = (detail as DetailWithMembers).membersState;
      if (!membersState || membersState.loading) {
        return false;
      }
    }

    return true;
  }

  async copyHostDetails(host: HostSummary): Promise<void> {
    if (!this.canCopyHostDetails(host)) {
      alert('Device details are still loading. Please wait and try again.');
      return;
    }

    try {
      const csv = this.buildHostDifferencesCSV(host);
      await navigator.clipboard.writeText(csv);
      alert('Device CSV copied to clipboard.');
    } catch (err) {
      console.error('Failed to copy device differences CSV', err);
      alert('Unable to copy device details to clipboard.');
    }
  }

  private getExpandedHosts(): HostSummary[] {
    return (this.hostSummaries ?? []).filter(host => !!host?.expanded);
  }

  private hasExpandedHostDetailsStillLoading(expandedHosts: HostSummary[]): boolean {
    return expandedHosts.some(host => host.loadingDetails || !host.detailsLoaded);
  }

  // Copies expanded host differences (host details and member rows) into CSV and writes to clipboard.
  async copyAllDifferences(): Promise<void> {
    const expandedHosts = this.getExpandedHosts();
    if (!expandedHosts.length) {
      alert('No devices are expanded. Expand one or more devices, then try Copy/Export again.');
      return;
    }
    if (this.hasExpandedHostDetailsStillLoading(expandedHosts)) {
      alert('Expanded device details are still loading. Please wait and try again.');
      return;
    }

    try {
      const csv = this.buildDifferencesCSV(expandedHosts);
      await navigator.clipboard.writeText(csv);
      alert('CSV copied to clipboard.');
    } catch (err) {
      console.error('Failed to copy differences CSV', err);
      alert('Unable to copy differences to clipboard.');
    }
  }

  // Exports expanded host differences as a downloadable file (Excel-friendly)
  exportAllDifferences(): void {
    const expandedHosts = this.getExpandedHosts();
    if (!expandedHosts.length) {
      alert('No devices are expanded. Expand one or more devices, then try Copy/Export again.');
      return;
    }
    if (this.hasExpandedHostDetailsStillLoading(expandedHosts)) {
      alert('Expanded device details are still loading. Please wait and try again.');
      return;
    }

    try {
      const csv = this.buildDifferencesCSV(expandedHosts);
      const bom = "\uFEFF"; // UTF-8 BOM improves Excel compatibility
      const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `sync-status-${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export differences CSV', err);
      alert('Unable to export CSV file.');
    }
  }

  // Builds the CSV string for the provided host list.
  private buildDifferencesCSV(hosts: HostSummary[]): string {
    const rows: string[][] = [];
    const header = ['Hostname', 'Group', 'Diff Type', 'Device / ASA Value', 'Gold Source Value'];
    rows.push(header);

    for (const host of hosts) {
      const details = host.detailsLoaded && host.details?.length ? host.details : [];
      for (const detail of details) {
        const group = detail.groupName ?? (detail as any).label ?? '';
        const diffType = this.getDiffType(detail);
        const deviceVal = this.formatValue(this.getCurrentValue(detail));
        const goldVal = this.formatValue(this.getExpectedValue(detail));
        rows.push([host.hostname, group, diffType, deviceVal, goldVal]);

        const ms = (detail as DetailWithMembers).membersState;
        if (ms?.members?.length) {
          for (const member of ms.members) {
            const memberDiff = this.getMemberDiffType(member);
            let deviceMemberVal = '';
            let goldMemberVal = '';
            const memberName = this.getMemberName(member);
            if (memberDiff === 'MISSING_IN_DEVICE') {
              deviceMemberVal = '—';
              goldMemberVal = memberName;
            } else if (memberDiff === 'EXTRA_IN_DEVICE' || memberDiff === 'EXTRA_DEVICE') {
              deviceMemberVal = memberName;
              goldMemberVal = '—';
            } else if (memberDiff === 'HASH_MISMATCH') {
              deviceMemberVal = memberName;
              goldMemberVal = 'Different value';
            } else {
              deviceMemberVal = memberName;
              goldMemberVal = '';
            }
            rows.push([host.hostname, group, memberDiff, deviceMemberVal, goldMemberVal]);
          }
        }
      }
    }

    return this.toCSV(rows);
  }

  private buildHostDifferencesCSV(host: HostSummary): string {
    const rows: string[][] = [];
    const header = ['Hostname', 'Group', 'Diff Type', 'Device / ASA Value', 'Gold Source Value'];
    rows.push(header);

    const details = host.detailsLoaded && host.details?.length ? host.details : [];
    for (const detail of details) {
      const group = detail.groupName ?? (detail as any).label ?? '';
      const diffType = this.getDiffType(detail);
      const deviceVal = this.formatValue(this.getCurrentValue(detail));
      const goldVal = this.formatValue(this.getExpectedValue(detail));
      rows.push([host.hostname, group, diffType, deviceVal, goldVal]);

      const ms = (detail as DetailWithMembers).membersState;
      if (ms?.members?.length) {
        for (const member of ms.members) {
          const memberDiff = this.getMemberDiffType(member);
          let deviceMemberVal = '';
          let goldMemberVal = '';
          const memberName = this.getMemberName(member);
          if (memberDiff === 'MISSING_IN_DEVICE') {
            deviceMemberVal = '—';
            goldMemberVal = memberName;
          } else if (memberDiff === 'EXTRA_IN_DEVICE' || memberDiff === 'EXTRA_DEVICE') {
            deviceMemberVal = memberName;
            goldMemberVal = '—';
          } else if (memberDiff === 'HASH_MISMATCH') {
            deviceMemberVal = memberName;
            goldMemberVal = 'Different value';
          } else {
            deviceMemberVal = memberName;
            goldMemberVal = '';
          }
          rows.push([host.hostname, group, memberDiff, deviceMemberVal, goldMemberVal]);
        }
      }
    }

    return this.toCSV(rows);
  }

  private toCSV(rows: string[][]): string {
    const escape = (val: string): string => {
      const needsQuotes = /[",\n]/.test(val);
      const escaped = val.replace(/"/g, '""');
      return needsQuotes ? `"${escaped}"` : escaped;
    };
    return rows.map(r => r.map(c => escape(c ?? '')).join(',')).join('\n');
  }
}
